<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <ContentCard :loading="loading">
    <template #header>
      <ContentCardSubheader :back-to="{ name: ReviewsRouteName.ReviewsAgencyPreferencesView }">
        {{ t('Reviews.agency.edit.preferencesTitle', { agencyName: agency.title }) }}
        <template #actions>
          <RouterLink
            class="btn-secondary-color-s mr-2"
            data-testid="agency-pref-cancel"
            :to="{ name: ReviewsRouteName.ReviewsAgencyPreferencesView, params: { agencyId: agency.id } }"
            @click="track('profile_update_cancel', { surface: 'editor', entity_type: 'service', entity_id: agency.id })"
          >
            {{ t('Reviews.edit.action.cancel') }}
          </RouterLink>
          <button class="btn-primary-color-s" data-testid="agency-pref-save" @click="onSubmit">
            {{ t('Reviews.edit.action.save') }}
          </button>
        </template>
      </ContentCardSubheader>
    </template>

    <div class="flex flex-col gap-y-4 pt-6 pb-10">
      <div class="flex items-start pb-6 text-main">
        <MdiSvg class="mr-2 inline-block" :path="mdiInformationOutline" />
        {{ t('Reviews.agency.edit.preferences.hint') }}
      </div>
      <PreferenceItemEdit
        id="contact-email-form"
        v-model="form.contactFormRecipients"
        data-testid="agency-pref-email-form"
        :description="t('Reviews.agency.view.preferences.contactEmails.description')"
        :title="t('Reviews.agency.view.preferences.contactEmails.title')"
      />
    </div>
  </ContentCard>
</template>

<script setup lang="ts">
import { mdiInformationOutline } from '@mdi/js'
import { MdiSvg } from '@ramp106/omrjs-core-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { inject, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import PreferenceItemEdit from '@/components/ReviewsManagement/Preferences/PreferenceItemEdit.vue'
import { useReviewsAgencyBreadcrumbs } from '@/composables/breadcrumbs/useReviewsAgencyBreadcrumbs'
import { type AgencyData, useUpdateReviewsAgencyMutation } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'
import { reloadReviewsAgencyKey } from '@/symbols/reviewsReloadKeys'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

const props = defineProps<{
  agency: AgencyData
}>()

const { t } = useI18n()
const router = useRouter()
const reloadReviewsAgency = inject(reloadReviewsAgencyKey) as () => void
const breadcrumbs = useReviewsAgencyBreadcrumbs(props.agency)

const {
  mutate: mutateAgency,
  loading,
  onDone,
  onError,
} = useUpdateReviewsAgencyMutation({
  clientId: 'reviews',
})

const form = reactive({
  contactFormRecipients: props.agency.contactFormRecipients ? [...props.agency.contactFormRecipients] : [],
})

async function onSubmit() {
  track('profile_update', { surface: 'editor', entity_type: 'service', entity_id: props.agency.id })
  await mutateAgency({
    id: props.agency.id,
    attributes: {
      contactFormRecipients: form.contactFormRecipients,
    },
  })
}

onDone(() => {
  reloadReviewsAgency()
  showNotification(t('Reviews.agency.notifications.savedSuccessfully', { name: props.agency.title }), 'success')
  router.push({ name: ReviewsRouteName.ReviewsAgencyPreferencesView, params: { agencyId: props.agency.id } })
})

onError((error) => showNotification(error.message, 'error'))
</script>
